var _dec, _class, _descriptor, _descriptor2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { getOwner } from '@ember/application';
import Service, { inject as service } from '@ember/service';
import { BrowserClient, defaultStackParser, getDefaultIntegrations, makeFetchTransport, Scope, getCurrentScope, getClient } from '@sentry/browser';
function hasStatusAndText(err) {
  return typeof err === 'object' && err !== null && 'status' in err && 'statusText' in err && typeof err.status === 'number' && typeof err.statusText === 'string';
}
export function isXhr(event, hint) {
  const exception = event.exception?.values?.[0] || {};
  const originalException = hint?.originalException;

  // Original narrow check for backward compatibility
  if (exception.type === 'Error' && exception.value === '[object Object]' && hasStatusAndText(originalException)) {
    return true;
  }

  // Broader check: Any error with HTTP status properties (catches fetch/XHR rejections)
  if (hasStatusAndText(originalException)) {
    return true;
  }

  // Check for HTTP error patterns in exception values
  const exceptionValue = exception.value;
  if (exceptionValue && typeof exceptionValue === 'string') {
    // Match HTTP status codes (4xx, 5xx) or common HTTP error messages
    if (/\b[45]\d{2}\b/.test(exceptionValue) ||
    // 4xx or 5xx status codes
    /fetch|xhr|ajax|http|request failed|network error|unauthorized|forbidden/i.test(exceptionValue)) {
      return true;
    }
  }

  // Check breadcrumbs for failed HTTP requests (status >= 400)
  if (event.breadcrumbs?.some(breadcrumb => (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr' || breadcrumb.category === 'http') && breadcrumb.data?.['status_code'] && breadcrumb.data['status_code'] >= 400)) {
    return true;
  }
  return false;
}

// Matches DuckDuckGo browser user agents (e.g., "DuckDuckGo/7" or "Ddg/18.3")
const DUCK_DUCK_GO_PATTERN = /duckduckgo|ddg\/\d/i;
export function isDuckDuckGo(event) {
  // Check browser context (may not always be populated)
  const browser = event.contexts?.['browser']?.['name'];
  if (browser && DUCK_DUCK_GO_PATTERN.test(browser)) {
    return true;
  }
  const userAgent = event.request?.headers?.['User-Agent'] || navigator.userAgent;
  if (userAgent && DUCK_DUCK_GO_PATTERN.test(userAgent)) {
    return true;
  }
  return false;
}

// Matches Instagram and Facebook in-app browser user agents
// Instagram: "Instagram 146.0.0.27.125 Android..."
// Facebook: "...FBAN/FB4A;FBAV/..." or "...FB_IAB..."
const SOCIAL_MEDIA_INAPP_PATTERN = /instagram|fban|fbav|fb_iab/i;
export function isSocialMediaInAppBrowser(event) {
  // Check browser context (may not always be populated)
  const browser = event.contexts?.['browser']?.['name'];
  if (browser && SOCIAL_MEDIA_INAPP_PATTERN.test(browser)) {
    return true;
  }
  const userAgent = event.request?.headers?.['User-Agent'] || navigator.userAgent;
  if (userAgent && SOCIAL_MEDIA_INAPP_PATTERN.test(userAgent)) {
    return true;
  }
  return false;
}
export function isVendorOnlyStackTrace(event) {
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || frames.length === 0) return false;
  return frames.every(frame => typeof frame.filename === 'string' && frame.filename.includes('assets/vendor'));
}

// checks if the parent sentry client is the multipass ui client
function isParentAppMultipassUi(client, multipassUiDsn) {
  const options = client?.getOptions?.();
  if (!options?.dsn) {
    return false;
  }
  return options.dsn.includes(multipassUiDsn);
}
let EmberMultipassLoggingService = (_dec = service('ember-multipass-config'), _class = class EmberMultipassLoggingService extends Service {
  get canLog() {
    return !this.multipassConfig.isProduction;
  }
  constructor(properties) {
    super(properties);
    _initializerDefineProperty(this, "cookies", _descriptor, this);
    _initializerDefineProperty(this, "multipassConfig", _descriptor2, this);
    _defineProperty(this, "scope", void 0);
    const config = getOwner(this).resolveRegistration('config:environment');
    const dsn = config['ember-multipass-components'].sentry.dsn;
    const sha = config['ember-multipass-components'].commitSha;
    const globalClient = getClient();
    this.scope = new Scope();
    if (!isParentAppMultipassUi(globalClient, dsn)) {
      const sentryClient = new BrowserClient({
        dsn,
        release: sha,
        environment: this.multipassConfig.environment,
        ignoreErrors: ['UNAUTHORIZED',
        // Caused by Stencil which Market uses
        'isProxied',
        // Caused by users cancelling the request.
        /^TypeError: cancelled/, /^TypeError: Failed to fetch/,
        // Firefox-specific error when users cancel or abort operations
        /^NS_ERROR_ABORT/,
        // Zoho PageSense third-party analytics
        /zp_token/,
        // Honey browser extension (Safari)
        /Honey\.app/, /Honey\.safariextension/, /\.appex\/Contents\/Resources/,
        // Expected business error when users exceed SCA verification attempts
        'ATTEMPT_NOT_ALLOWED',
        // IndexedDB errors in restricted contexts (private browsing, sandboxed iframes)
        /Unable to establish IDB database file/,
        // Stack overflow with no actionable stacktrace (not from our static bundles)
        /Maximum call stack size exceeded/],
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        integrations: getDefaultIntegrations({}),
        beforeSend(event, hint) {
          // Ignore rejected Promises caused by XHR request failures - they're noisy and we have backend logs for that.
          if (isXhr(event, hint)) {
            return null;
          }
          // Filter all errors from DuckDuckGo browsers
          if (isDuckDuckGo(event)) {
            return null;
          }
          // Filter all errors from Instagram/Facebook in-app browsers (Android WebViews with known issues)
          if (isSocialMediaInAppBrowser(event)) {
            return null;
          }
          // Filter errors with stack traces entirely in vendor-bundled code (not actionable)
          if (isVendorOnlyStackTrace(event)) {
            return null;
          }
          return event;
        },
        replaysOnErrorSampleRate: 0,
        replaysSessionSampleRate: 0
      });
      this.scope.setClient(sentryClient);
      sentryClient.init();
      this.scope.setTag('logging_source', 'ember-multipass-components');
    } else {
      this.scope = getCurrentScope();
    }
    this.scope.setExtra('hostname', window.location.hostname);
    this.scope.setExtra('pathname', window.location.pathname);
    if (!this.scope.getUser()?.id) {
      this.scope.setUser({
        id: this.cookies.read('_savt') || null
      });
    }
  }
  log(...args) {
    if (!this.canLog) {
      return;
    }
    console.log(...args);
  }
  error(...args) {
    if (!this.canLog) {
      return;
    }
    console.error(...args);
  }
  sentryMessage(message, severity) {
    this.scope.captureMessage(message, severity ?? 'warning', undefined);
  }
  sentryError(error) {
    if (error instanceof Error) {
      this.scope.captureException(error, undefined);
    } else {
      this.scope.captureException(new Error(String(error)), undefined);
    }
  }
  addNavigationBreadcrumb(destination, category = 'navigation') {
    this.scope.addBreadcrumb({
      category,
      message: `Navigated to ${destination}`,
      level: 'info'
    });
  }
  addComponentBreadcrumb(name, category = 'component') {
    this.scope.addBreadcrumb({
      category,
      message: `${name} component initialized`,
      level: 'info'
    });
  }
  addFeatureFlagTag(flagName, flagValue) {
    this.scope.setTag(flagName, flagValue);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "cookies", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "multipassConfig", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { EmberMultipassLoggingService as default };